import { useState } from 'react';

export default function Workflows() {
  const [activeCard, setActiveCard] = useState<'before' | 'after' | null>(null);

  return (
    <section id="workflows-section" className="py-16 sm:py-24 bg-gray-50 relative overflow-hidden">
      {/* Decorative dots */}
      <div className="absolute inset-0 dots-pattern"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        {/* Workflow 1 - Limpieza/Mantenimiento */}
        <div className="mb-16 sm:mb-24">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
            Antes vs. Después de PreBedAI
          </h2>
          <p className="text-gray-600 text-center mb-8 sm:mb-12 max-w-2xl mx-auto text-sm sm:text-base px-2">
            Un caso concreto: gestión de limpieza y disponibilidad de camas
          </p>

          {/* Innovative Before/After Cards */}
          <div className="relative mb-8 sm:mb-12">
            {/* Connection Animation - Central Arrow - Solo desktop */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 hidden lg:flex flex-col items-center">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-2xl">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary-400 to-primary-500 animate-ping opacity-30"></div>
                  <i className="ri-arrow-right-double-line text-white text-3xl animate-pulse"></i>
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-3 font-medium tracking-wider uppercase bg-white px-3 py-1 rounded-full shadow-sm">Transformación</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-4 sm:gap-8 lg:gap-16">
              {/* BEFORE Card */}
              <div 
                className={`group relative transition-all duration-500 ${activeCard === 'before' ? 'scale-[1.02]' : ''}`}
                onMouseEnter={() => setActiveCard('before')}
                onMouseLeave={() => setActiveCard(null)}
              >
                <div className="relative bg-gradient-to-br from-red-50 to-red-100/50 rounded-xl sm:rounded-2xl border-2 border-red-200/60 overflow-hidden transition-all duration-500 group-hover:border-red-300 group-hover:shadow-lg">
                  {/* Header */}
                  <div className="relative bg-white/70 backdrop-blur-sm px-4 sm:px-6 py-3 sm:py-4 border-b border-red-200/60">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-100 rounded-xl flex items-center justify-center shadow-sm">
                        <i className="ri-close-circle-line text-red-600 text-lg sm:text-xl"></i>
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold text-red-600">ANTES</h3>
                        <p className="text-xs text-red-500/80">Proceso manual tradicional</p>
                      </div>
                    </div>
                  </div>

                  {/* Image */}
                  <div className="relative h-40 sm:h-[280px] overflow-hidden">
                    <img
                      src="/images/workflow-before.jpg"
                      alt="Flujo caótico anterior con llamadas y post-its"
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-red-950/90 via-red-950/40 to-transparent"></div>
                    
                    {/* Time badge */}
                    <div className="absolute top-2 sm:top-4 right-2 sm:right-4 bg-red-500 backdrop-blur-sm px-2 sm:px-4 py-1 sm:py-2 rounded-full shadow-lg">
                      <p className="text-white font-bold text-xs sm:text-sm flex items-center gap-1 sm:gap-2">
                        <i className="ri-time-line"></i>
                        14-18 horas
                      </p>
                    </div>
                  </div>

                  {/* Problems list */}
                  <div className="p-4 sm:p-6 space-y-2 sm:space-y-3 bg-white/60 backdrop-blur-sm">
                    {[
                      { icon: 'ri-phone-line', text: 'Llamadas cruzadas entre departamentos' },
                      { icon: 'ri-sticky-note-line', text: 'Post-its y listas en papel que se pierden' },
                      { icon: 'ri-error-warning-line', text: 'Información perdida entre turnos' },
                      { icon: 'ri-question-line', text: 'Sin trazabilidad: nadie sabe quién hizo qué' },
                    ].map((problem, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center gap-2 sm:gap-3 text-gray-700 group-hover:text-gray-900 transition-colors"
                      >
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                          <i className={`${problem.icon} text-red-600 text-sm sm:text-base`}></i>
                        </div>
                        <span className="text-xs sm:text-sm">{problem.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Mobile transformation indicator */}
              <div className="lg:hidden flex justify-center my-2">
                <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-full border border-gray-200 shadow-sm">
                  <span className="text-red-500 font-medium text-sm">Caos</span>
                  <i className="ri-arrow-right-line text-gray-600"></i>
                  <span className="text-primary-500 font-medium text-sm">Control</span>
                </div>
              </div>

              {/* AFTER Card */}
              <div 
                className={`group relative transition-all duration-500 ${activeCard === 'after' ? 'scale-[1.02]' : ''}`}
                onMouseEnter={() => setActiveCard('after')}
                onMouseLeave={() => setActiveCard(null)}
              >
                <div className="relative bg-gradient-to-br from-primary-50 to-primary-100/50 rounded-xl sm:rounded-2xl border-2 border-primary-200/60 overflow-hidden transition-all duration-500 group-hover:border-primary-300 group-hover:shadow-lg">
                  {/* Header */}
                  <div className="relative bg-white/70 backdrop-blur-sm px-4 sm:px-6 py-3 sm:py-4 border-b border-primary-200/60">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary-100 rounded-xl flex items-center justify-center shadow-sm">
                        <i className="ri-check-double-line text-primary-600 text-lg sm:text-xl"></i>
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold text-primary-600">DESPUÉS</h3>
                        <p className="text-xs text-primary-500/80">Con PreBedAI</p>
                      </div>
                    </div>
                  </div>

                  {/* Image */}
                  <div className="relative h-40 sm:h-[280px] overflow-hidden">
                    <img
                      src="/images/workflow-after.jpg"
                      alt="Interfaz PreBedAI con mapa de camas en tiempo real"
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/90 via-emerald-950/40 to-transparent"></div>
                    
                    {/* Time badge */}
                    <div className="absolute top-2 sm:top-4 right-2 sm:right-4 bg-primary-500 backdrop-blur-sm px-2 sm:px-4 py-1 sm:py-2 rounded-full shadow-lg">
                      <p className="text-white font-bold text-xs sm:text-sm flex items-center gap-1 sm:gap-2">
                        <i className="ri-timer-flash-line"></i>
                        2-3 horas
                      </p>
                    </div>

                    {/* Reduction badge */}
                    <div className="absolute top-2 sm:top-4 left-2 sm:left-4 bg-primary-500 backdrop-blur-sm px-2 sm:px-3 py-1 sm:py-1.5 rounded-full shadow-lg">
                      <p className="text-white font-bold text-xs flex items-center gap-1">
                        <i className="ri-arrow-down-line"></i>
                        -80%
                      </p>
                    </div>
                  </div>

                  {/* Solutions list */}
                  <div className="p-4 sm:p-6 space-y-2 sm:space-y-3 bg-white/60 backdrop-blur-sm">
                    {[
                      { icon: 'ri-notification-3-line', text: 'Notificaciones automáticas al equipo correcto' },
                      { icon: 'ri-message-3-line', text: 'Confirmación desde el mensaje (sin llamadas)' },
                      { icon: 'ri-refresh-line', text: 'Actualización automática de estados en el mapa' },
                      { icon: 'ri-file-list-3-line', text: 'Trazabilidad completa: quién, qué, cuándo' },
                    ].map((solution, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center gap-2 sm:gap-3 text-gray-700 group-hover:text-gray-900 transition-colors"
                      >
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                          <i className={`${solution.icon} text-primary-600 text-sm sm:text-base`}></i>
                        </div>
                        <span className="text-xs sm:text-sm">{solution.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline Flow with Real Times */}
          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-8 border border-gray-200 shadow-sm">
            <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 text-center">Flujo Completo en Tiempo Real</h4>
            <p className="text-center text-gray-600 mb-6 sm:mb-8 text-xs sm:text-sm">Ejemplo de alta confirmada a cama disponible</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-4">
              {/* Card 1 - Alta confirmada */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 sm:p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-[#F59E0B] to-[#D97706] rounded-full flex items-center justify-center shadow-lg">
                    <i className="ri-hospital-line text-white text-2xl sm:text-3xl"></i>
                  </div>
                </div>
                <p className="text-center text-gray-500 font-bold text-base sm:text-lg mb-1">09:15</p>
                <h5 className="text-center font-bold text-gray-900 text-base sm:text-lg mb-2">Alta confirmada</h5>
                <p className="text-center text-gray-600 text-xs sm:text-sm">El médico confirma el alta del paciente</p>
              </div>

              {/* Card 2 - Cama pendiente */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 sm:p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-[#F59E0B] to-[#D97706] rounded-full flex items-center justify-center shadow-lg">
                    <i className="ri-edit-line text-white text-2xl sm:text-3xl"></i>
                  </div>
                </div>
                <p className="text-center text-gray-500 font-bold text-base sm:text-lg mb-1">09:16</p>
                <h5 className="text-center font-bold text-gray-900 text-base sm:text-lg mb-2">Cama pendiente</h5>
                <p className="text-center text-gray-600 text-xs sm:text-sm">Cama como pendiente de limpieza</p>
              </div>

              {/* Card 3 - Notificación */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 sm:p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-[#06B6D4] to-[#0891B2] rounded-full flex items-center justify-center shadow-lg">
                    <i className="ri-notification-3-line text-white text-2xl sm:text-3xl"></i>
                  </div>
                </div>
                <p className="text-center text-gray-500 font-bold text-base sm:text-lg mb-1">09:17</p>
                <h5 className="text-center font-bold text-gray-900 text-base sm:text-lg mb-2">Notificación</h5>
                <p className="text-center text-gray-600 text-xs sm:text-sm">El equipo pertinente recibe una notificación automática</p>
              </div>

              {/* Card 4 - Confirmación */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 sm:p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-[#8B5CF6] to-[#7C3AED] rounded-full flex items-center justify-center shadow-lg">
                    <i className="ri-checkbox-circle-line text-white text-2xl sm:text-3xl"></i>
                  </div>
                </div>
                <p className="text-center text-gray-500 font-bold text-base sm:text-lg mb-1">09:45</p>
                <h5 className="text-center font-bold text-gray-900 text-base sm:text-lg mb-2">Confirmación</h5>
                <p className="text-center text-gray-600 text-xs sm:text-sm">Personal de limpieza confirma la tarea completada desde su dispositivo</p>
              </div>

              {/* Card 5 - Disponible */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 sm:p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-[#10B981] to-[#059669] rounded-full flex items-center justify-center shadow-lg">
                    <i className="ri-check-double-line text-white text-2xl sm:text-3xl"></i>
                  </div>
                </div>
                <p className="text-center text-gray-500 font-bold text-base sm:text-lg mb-1">09:46</p>
                <h5 className="text-center font-bold text-gray-900 text-base sm:text-lg mb-2">Disponible</h5>
                <p className="text-center text-gray-600 text-xs sm:text-sm">La cama aparece como disponible en el mapa en tiempo real</p>
              </div>
            </div>

            <div className="mt-6 sm:mt-8 text-center space-y-1 sm:space-y-2">
              <p className="text-xl sm:text-3xl font-bold">
                <span className="text-red-500">De 14-18 horas</span> a <span className="text-green-500">2-3 horas promedio</span>
              </p>
            </div>
          </div>
        </div>

        {/* Workflow 2 - Reunión Matutina */}
        <div>
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-8 sm:mb-12 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
            La Reunión que Ahora Dura <span className="text-primary-500">10-15 Minutos</span>
          </h2>

          <div className="grid lg:grid-cols-2 gap-4 sm:gap-8 mb-8 sm:mb-12">
            {/* Traditional Meeting */}
            <div className="bg-gray-800 rounded-xl sm:rounded-2xl p-6 sm:p-10 border border-gray-700">
              <h4 className="text-lg sm:text-xl font-bold text-white mb-5 sm:mb-7">Reunión Tradicional</h4>
              <div className="space-y-4 sm:space-y-5">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-7 h-7 sm:w-9 sm:h-9 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-white text-base sm:text-lg"></i>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm sm:text-base">45-60 minutos (o más)</p>
                    <p className="text-xs sm:text-sm text-gray-300">Buscando información dispersa</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-7 h-7 sm:w-9 sm:h-9 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-question-line text-white text-base sm:text-lg"></i>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm sm:text-base">Datos incompletos</p>
                    <p className="text-xs sm:text-sm text-gray-300">Información que no coincide</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-7 h-7 sm:w-9 sm:h-9 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-chat-3-line text-white text-base sm:text-lg"></i>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm sm:text-base">Decisiones lentas</p>
                    <p className="text-xs sm:text-sm text-gray-300">Sin criterios claros</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-7 h-7 sm:w-9 sm:h-9 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-team-line text-white text-base sm:text-lg"></i>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm sm:text-base">Equipo frustrado</p>
                    <p className="text-xs sm:text-sm text-gray-300">Sensación de pérdida de tiempo</p>
                  </div>
                </div>
              </div>
            </div>

            {/* With PreBedAI */}
            <div className="bg-gradient-to-br from-[#1A2332] to-[#0A1628] rounded-xl sm:rounded-2xl p-6 sm:p-10 border border-gray-600">
              <h4 className="text-lg sm:text-xl font-bold text-white mb-5 sm:mb-7">Con PreBedAI</h4>
              <div className="space-y-4 sm:space-y-5">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-7 h-7 sm:w-9 sm:h-9 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-dashboard-line text-white text-base sm:text-lg"></i>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm sm:text-base">10-15 minutos</p>
                    <p className="text-xs sm:text-sm text-gray-300">Dashboard con toda la información</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-7 h-7 sm:w-9 sm:h-9 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-brain-line text-white text-base sm:text-lg"></i>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm sm:text-base">Predicciones IA visibles</p>
                    <p className="text-xs sm:text-sm text-gray-300">Criterios transparentes y editables</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-7 h-7 sm:w-9 sm:h-9 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-flashlight-line text-white text-base sm:text-lg"></i>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm sm:text-base">Decisiones rápidas</p>
                    <p className="text-xs sm:text-sm text-gray-300">Tareas asignadas automáticamente</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-7 h-7 sm:w-9 sm:h-9 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-emotion-happy-line text-white text-base sm:text-lg"></i>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm sm:text-base">Equipo más satisfecho</p>
                    <p className="text-xs sm:text-sm text-gray-300">Más tiempo para tareas clínicas</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-6 sm:mt-8 bg-white/5 backdrop-blur-sm rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/10">
            <p className="text-xs sm:text-sm text-gray-400 text-center">
              <i className="ri-information-line text-[#06B6D4] mr-2"></i>
              Los tiempos de reducción son aproximados y dependen del proceso de cada hospital.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
